import React, { useCallback, useEffect } from "react";

import FileManager, {
  ContextMenu,
  Item,
  Permissions,
} from "devextreme-react/file-manager";
import { useTranslation } from "react-i18next";

import { useWindowDimensions } from "@app/contexts";
import { useAppDispatch, useAppSelector } from "@app/state";
import { downloadFile, selectFileSystemRoots } from "@app/state/units/fileSys";
import { startScript } from "@app/state/units/ui/fileops";

import "./fileExplorer.scss";

import type { FileManagerRef } from "devextreme-react/file-manager";
type PathInfoItem = {
  key: string;
  name: string;
};

type DataItem = {
  name: string;
  size: number;
  dateModified: string;
  thumbnail: string;
  isDirectory: boolean;
};

type TFileObject = {
  name: string;
  pathInfo: PathInfoItem[];
  parentPath: string;
  relativeName: string;
  key: string;
  path: string;
  pathKeys: string[];
  isDirectory: boolean;
  size: number;
  dateModified: string;
  thumbnail: string;
  tooltipText: string;
  dataItem: DataItem;
};

function assembleFilePath(fileObj: TFileObject) {
  if (!fileObj.pathInfo || !Array.isArray(fileObj.pathInfo)) {
    throw new Error("Invalid file object format");
  }
  const pathComponents = fileObj.pathInfo.map((item) => item.name);
  const assembledPath = pathComponents.join("/");
  return assembledPath + "/" + fileObj.name;
}

export const FileExplorer = ({ roots }: { roots: string[] }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const fmRef = React.useRef<FileManagerRef>(null);
  const filesToDownloadRef = React.useRef<string[]>([]);
  const scriptFiles = React.useRef<string[]>([]);

  const { drawerHeight } = useWindowDimensions();
  const files = useAppSelector((s) =>
    selectFileSystemRoots(s, { roots: [...roots, "svn", "COMMON"] })
  );
  const onIntemDownloading = useCallback((e: any) => {
    e.cancel = true; // Cancel the download
    const nextFile = assembleFilePath(e.item);
    // Ensure unique additions
    if (!filesToDownloadRef.current.includes(nextFile)) {
      filesToDownloadRef.current.push(nextFile);
    }
    const selectedItems = fmRef.current?.instance()?.getSelectedItems();

    if (filesToDownloadRef.current.length === selectedItems?.length) {
      // Process all the files here
      dispatch(downloadFile(filesToDownloadRef.current));
      filesToDownloadRef.current = []; // Reset after processing
    }
  }, []);

  const onContextMenuShowing = useCallback((e: any) => {
    const isSvn = e.component.option().currentPathKeys[0] === "svn";
    const isSql = e.component.option().focusedItemKey?.endsWith(".sql");
    if (e.component.option()?.contextMenu?.items) {
      const ourMenuItem = e.component
        .option()
        .contextMenu.items.find(
          (item: any) => item.name === "from_this_script"
        );
      if (ourMenuItem) {
        ourMenuItem.disabled = !isSvn || !isSql;
      }
    }
  }, []);
  return (
    <div>
      <FileManager
        ref={fmRef}
        height={(drawerHeight || 0) - 60}
        selectionMode="multiple"
        fileSystemProvider={files}
        onItemDownloading={onIntemDownloading}
        onContextMenuShowing={onContextMenuShowing}
        onContextMenuItemClick={(e) => {
          const theOperation = e.itemData?.name || e.itemData?.options?.name;
          if (theOperation) {
            if (
              [
                // "from_this_script",
                "each_to_the_end",
                "each_month",
                "each_3_months",
                "each_year",
                "single_batch",
                "two_batches",
                "three_batches",
              ].includes(theOperation)
            ) {
              const timespan = theOperation;
              if (e.fileSystemItem) {
                const fileName = e.fileSystemItem.pathKeys.at(-1) as string;
                dispatch(startScript({ fileName, timespan }));
              }
            }
          }
        }}
      >
        <Permissions
          create={false}
          copy={false}
          move={false}
          delete={false}
          rename={false}
          upload={false}
          download={true}
        ></Permissions>
        <ContextMenu>
          <Item name="refresh" />
          <Item name="download" />
          <Item
            options={{ name: "from_this_script" }}
            text={t("from_this_script")}
            // beginGroup="true"
            icon={"preferences"}
          >
            <Item
              options={{
                name: "each_to_the_end",
              }}
              text={t("each_to_the_end")}
              // beginGroup="true"
            />
            <Item options={{ name: "each_month" }} text={t("each_month")} />
            <Item
              options={{ name: "each_3_months" }}
              text={t("each_3_months")}
            />
            <Item options={{ name: "each_year" }} text={t("each_year")} />
            <Item name="separator" />
            <Item options={{ name: "single_batch" }} text={t("single_batch")} />
            <Item options={{ name: "two_batches" }} text={t("two_batches")} />
            <Item
              options={{ name: "three_batches" }}
              text={t("three_batches")}
            />
          </Item>
        </ContextMenu>
      </FileManager>
    </div>
  );
};
